import { useEffect, useMemo, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { Camera, CreditCard, Download, FileDown, KeyRound, Loader2, LogOut, Mail, ShieldCheck, ShieldOff, Trash2, Upload } from "lucide-react";
import { toast } from "sonner";
import { useTranslation } from "react-i18next";
import { Alert, AlertDescription, AlertTitle } from "@/components/ui/alert";
import { Avatar, AvatarFallback, AvatarImage } from "@/components/ui/avatar";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Separator } from "@/components/ui/separator";
import { Switch } from "@/components/ui/switch";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import { Textarea } from "@/components/ui/textarea";
import { StatusBadge } from "@/components/billing/StatusBadge";
import { supabase } from "@/integrations/supabase/client";
import { useAuth } from "@/hooks/useAuth";
import { useLanguagePreference } from "@/hooks/useLanguagePreference";
import { db, formatDate, invoiceDownloadHref, money } from "@/lib/billing";
import { blankProfileForm, fetchUserProfile, profileToForm, saveOwnProfile, validateProfileForm, type ProfileForm } from "@/lib/userProfile";
import { getInitials } from "@/lib/adminUsers";

const MAX_AVATAR_SIZE = 2 * 1024 * 1024;
const AVATAR_TYPES = ["image/jpeg", "image/png", "image/webp"];

export default function ProfilePage() {
  const { user, signOut } = useAuth();
  const { t } = useTranslation();
  const { changeLanguage, options } = useLanguagePreference();
  const queryClient = useQueryClient();
  const [form, setForm] = useState<ProfileForm>(blankProfileForm);
  const [passwordForm, setPasswordForm] = useState({ current: "", next: "", confirm: "" });
  const [errors, setErrors] = useState<Partial<Record<keyof ProfileForm, string>>>({});

  const { data: profile, isLoading, error } = useQuery({
    queryKey: ["my-profile", user?.id],
    queryFn: () => fetchUserProfile(user!.id, user?.email),
    enabled: !!user,
  });
  const { data: notificationPreferences } = useQuery({
    queryKey: ["my-notification-preferences", user?.id],
    queryFn: async () => {
      const { data, error: preferencesError } = await db
        .from("notification_preferences")
        .select("*")
        .eq("user_id", user!.id)
        .maybeSingle();
      if (preferencesError) throw preferencesError;
      return data ?? {
        invoice_emails: true,
        payment_alerts: true,
        renewal_reminders: true,
        expiration_warnings: true,
        marketing_emails: false,
        service_alerts: true,
        language: form.preferred_language,
      };
    },
    enabled: !!user,
  });

  useEffect(() => {
    if (profile) setForm(profileToForm(profile));
  }, [profile]);

  const saveProfile = useMutation({
    mutationFn: async () => {
      if (!user) throw new Error("Session required");
      const nextErrors = validateProfileForm(form);
      setErrors(nextErrors);
      if (Object.keys(nextErrors).length) throw new Error("Veuillez corriger les champs du profil.");
      const saved = await saveOwnProfile(user.id, form);
      await changeLanguage(saved.preferred_language);
      return saved;
    },
    onSuccess: async () => {
      toast.success("Profil enregistre");
      await queryClient.invalidateQueries({ queryKey: ["my-profile", user?.id] });
    },
    onError: (err: any) => toast.error(err?.message ?? "Impossible d'enregistrer le profil"),
  });

  const uploadAvatar = useMutation({
    mutationFn: async (file: File) => {
      if (!user) throw new Error("Session required");
      if (!AVATAR_TYPES.includes(file.type)) throw new Error("Format accepte: JPG, PNG ou WebP.");
      if (file.size > MAX_AVATAR_SIZE) throw new Error("L'avatar doit faire moins de 2 Mo.");

      const extension = file.name.split(".").pop()?.toLowerCase() || "png";
      const path = `${user.id}/avatar.${extension}`;
      const { error: uploadError } = await supabase.storage.from("avatars").upload(path, file, {
        cacheControl: "3600",
        contentType: file.type,
        upsert: true,
      });
      if (uploadError) throw uploadError;

      const { data } = supabase.storage.from("avatars").getPublicUrl(path);
      const nextForm = { ...form, avatar_url: data.publicUrl };
      setForm(nextForm);
      await saveOwnProfile(user.id, nextForm);
      return data.publicUrl;
    },
    onSuccess: async () => {
      toast.success("Avatar mis a jour");
      await queryClient.invalidateQueries({ queryKey: ["my-profile", user?.id] });
    },
    onError: (err: any) => toast.error(err?.message ?? "Impossible de mettre a jour l'avatar"),
  });

  const removeAvatar = useMutation({
    mutationFn: async () => {
      if (!user) throw new Error("Session required");
      const nextForm = { ...form, avatar_url: "" };
      setForm(nextForm);
      await saveOwnProfile(user.id, nextForm);
    },
    onSuccess: async () => {
      toast.success("Avatar supprime");
      await queryClient.invalidateQueries({ queryKey: ["my-profile", user?.id] });
    },
    onError: (err: any) => toast.error(err?.message ?? "Impossible de supprimer l'avatar"),
  });

  const changePassword = useMutation({
    mutationFn: async () => {
      if (!user?.email) throw new Error("Adresse e-mail introuvable pour cette session.");
      if (passwordForm.next.length < 8) throw new Error("Le nouveau mot de passe doit contenir au moins 8 caracteres.");
      if (passwordForm.next !== passwordForm.confirm) throw new Error("Les mots de passe ne correspondent pas.");
      const { error: verifyError } = await supabase.auth.signInWithPassword({
        email: user.email,
        password: passwordForm.current,
      });
      if (verifyError) throw new Error("Le mot de passe actuel est incorrect.");
      const { error: updateError } = await supabase.auth.updateUser({ password: passwordForm.next });
      if (updateError) throw updateError;
    },
    onSuccess: () => {
      toast.success("Mot de passe mis a jour");
      setPasswordForm({ current: "", next: "", confirm: "" });
    },
    onError: (err: any) => toast.error(err?.message ?? "Impossible de changer le mot de passe"),
  });

  const saveNotificationPreference = useMutation({
    mutationFn: async (patch: Record<string, boolean | string>) => {
      if (!user) throw new Error("Session required");
      const { error: preferenceError } = await db.from("notification_preferences").upsert({
        user_id: user.id,
        invoice_emails: notificationPreferences?.invoice_emails ?? true,
        payment_alerts: notificationPreferences?.payment_alerts ?? true,
        renewal_reminders: notificationPreferences?.renewal_reminders ?? true,
        expiration_warnings: notificationPreferences?.expiration_warnings ?? true,
        marketing_emails: notificationPreferences?.marketing_emails ?? false,
        service_alerts: notificationPreferences?.service_alerts ?? true,
        language: form.preferred_language,
        ...patch,
      }, { onConflict: "user_id" });
      if (preferenceError) throw preferenceError;
    },
    onSuccess: () => {
      toast.success("Preferences de notification mises a jour");
      queryClient.invalidateQueries({ queryKey: ["my-notification-preferences", user?.id] });
    },
    onError: (err: any) => toast.error(err?.message ?? "Impossible de mettre a jour les notifications"),
  });

  const latestPackage = profile?.subscription?.packages;
  const latestInvoice = profile?.latest_invoice;
  const packageFeatures = latestPackage?.features && typeof latestPackage.features === "object"
    ? Object.entries(latestPackage.features).filter(([, enabled]) => Boolean(enabled)).map(([key]) => key.replace(/_/g, " "))
    : [];
  const billingCycle = latestPackage?.billing_period ?? "monthly";
  const autoRenew = profile?.subscription ? !profile.subscription.cancel_at_period_end : false;
  const mfaSupported = Boolean((supabase.auth as any).mfa);
  const displayName = form.full_name || user?.email || "Compte";
  const subscriptionDetails = useMemo(() => [
    ["Cycle", billingCycle === "yearly" ? "Annuel" : "Mensuel"],
    ["Renouvellement", formatDate(profile?.subscription?.current_period_end)],
    ["Expiration", formatDate(profile?.subscription?.trial_end ?? profile?.subscription?.current_period_end)],
    ["Paiement", profile?.payment_status ?? "Non disponible"],
    ["Renouvellement auto", autoRenew ? "Active" : "Desactive"],
    ["Derniere facture", latestInvoice?.invoice_number ?? "Non disponible"],
  ], [autoRenew, billingCycle, profile, latestInvoice?.invoice_number]);
  const generatedInvoiceHref = latestInvoice ? invoiceDownloadHref(latestInvoice) : null;

  if (isLoading) {
    return <div className="flex min-h-screen items-center justify-center"><Loader2 className="h-8 w-8 animate-spin text-muted-foreground" /></div>;
  }

  return (
    <div className="min-h-screen bg-background">
      <header className="sticky top-0 z-30 flex h-14 items-center justify-between border-b border-border bg-background/80 px-6 backdrop-blur-xl">
        <div>
          <h1 className="text-lg font-semibold text-foreground">{t("profile.title")}</h1>
          <p className="text-xs text-muted-foreground">{t("profile.subtitle")}</p>
        </div>
        <Button variant="outline" size="sm" onClick={() => void signOut()}><LogOut className="mr-2 h-4 w-4" />{t("nav.signOut")}</Button>
      </header>

      <main className="space-y-6 p-6">
        {error && (
          <Alert variant="destructive">
            <AlertTitle>Erreur</AlertTitle>
            <AlertDescription>{(error as Error).message}</AlertDescription>
          </Alert>
        )}

        <Card>
          <CardContent className="flex flex-col gap-5 p-5 md:flex-row md:items-center md:justify-between">
            <div className="flex items-center gap-4">
              <Avatar className="h-20 w-20">
                <AvatarImage src={form.avatar_url || undefined} />
                <AvatarFallback>{getInitials(displayName)}</AvatarFallback>
              </Avatar>
              <div>
                <h2 className="text-xl font-semibold">{displayName}</h2>
                <p className="text-sm text-muted-foreground">{user?.email}</p>
                <div className="mt-2 flex flex-wrap gap-2">
                  <StatusBadge status={profile?.account_status ?? "unknown"} />
                  <Badge variant={profile?.email_verified ? "default" : "secondary"}>
                    {profile?.email_verified ? "E-mail verifie" : "E-mail en attente"}
                  </Badge>
                </div>
              </div>
            </div>
            <div className="flex flex-wrap gap-2">
              <label>
                <input
                  type="file"
                  accept="image/png,image/jpeg,image/webp"
                  className="sr-only"
                  onChange={(event) => {
                    const file = event.target.files?.[0];
                    if (file) uploadAvatar.mutate(file);
                    event.target.value = "";
                  }}
                />
                <Button type="button" variant="outline" disabled={uploadAvatar.isPending} asChild>
                  <span><Upload className="mr-2 h-4 w-4" />Changer l'avatar</span>
                </Button>
              </label>
              <Button type="button" variant="outline" onClick={() => removeAvatar.mutate()} disabled={!form.avatar_url || removeAvatar.isPending}>
                <Trash2 className="mr-2 h-4 w-4" />Supprimer
              </Button>
            </div>
          </CardContent>
        </Card>

        <Tabs defaultValue="profile" className="space-y-4">
          <TabsList className="flex h-auto flex-wrap justify-start">
            <TabsTrigger value="profile">{t("profile.tabs.profile")}</TabsTrigger>
            <TabsTrigger value="subscription">{t("profile.tabs.subscription")}</TabsTrigger>
            <TabsTrigger value="security">{t("profile.tabs.security")}</TabsTrigger>
            <TabsTrigger value="language">{t("profile.tabs.language")}</TabsTrigger>
            <TabsTrigger value="notifications">{t("profile.tabs.notifications")}</TabsTrigger>
            <TabsTrigger value="privacy">{t("profile.tabs.privacy")}</TabsTrigger>
          </TabsList>

          <TabsContent value="profile">
            <Card>
              <CardHeader>
                <CardTitle>Informations personnelles</CardTitle>
                <CardDescription>L'e-mail est affiche pour reference et reste gere par le flux securise Delapresse.</CardDescription>
              </CardHeader>
              <CardContent className="grid gap-4 md:grid-cols-2">
                <Field label="Nom complet" error={errors.full_name}><Input value={form.full_name} onChange={(e) => setForm({ ...form, full_name: e.target.value })} /></Field>
                <Field label="E-mail"><Input value={user?.email ?? ""} disabled /></Field>
                <Field label="Telephone" error={errors.phone_number}><Input value={form.phone_number} onChange={(e) => setForm({ ...form, phone_number: e.target.value })} /></Field>
                <Field label="Entreprise"><Input value={form.company_name} onChange={(e) => setForm({ ...form, company_name: e.target.value })} /></Field>
                <Field label="Pays"><Input value={form.country} onChange={(e) => setForm({ ...form, country: e.target.value })} /></Field>
                <Field label="Date de creation"><Input value={formatDate(profile?.created_at)} disabled /></Field>
                <div className="md:col-span-2">
                  <Field label="Adresse"><Textarea value={form.address} onChange={(e) => setForm({ ...form, address: e.target.value })} /></Field>
                </div>
                <div className="md:col-span-2">
                  <Button onClick={() => saveProfile.mutate()} disabled={saveProfile.isPending}>
                    {saveProfile.isPending ? <Loader2 className="mr-2 h-4 w-4 animate-spin" /> : <Camera className="mr-2 h-4 w-4" />}
                    Enregistrer le profil
                  </Button>
                </div>
              </CardContent>
            </Card>
          </TabsContent>

          <TabsContent value="subscription" className="grid gap-6 lg:grid-cols-3">
            <Card className="lg:col-span-1">
              <CardHeader>
                <CardTitle>{latestPackage?.name ?? "Aucun package"}</CardTitle>
                <CardDescription>{latestPackage?.description ?? "Aucun abonnement actif n'est lie a ce compte."}</CardDescription>
              </CardHeader>
              <CardContent className="space-y-4">
                {profile?.subscription && <StatusBadge status={profile.subscription.status} />}
                {latestPackage && (
                  <div className="rounded-md border border-border p-4">
                    <p className="text-2xl font-bold">{latestPackage.is_enterprise ? "Sur mesure" : money(latestPackage.monthly_price, latestPackage.currency)}</p>
                    <p className="text-xs text-muted-foreground">{billingCycle === "yearly" ? "par an" : "par mois"}</p>
                  </div>
                )}
                {packageFeatures.length > 0 && (
                  <div className="rounded-md border border-border p-4">
                    <p className="text-sm font-medium">Fonctionnalites incluses</p>
                    <div className="mt-3 flex flex-wrap gap-2">
                      {packageFeatures.slice(0, 8).map((feature) => <Badge key={feature} variant="outline" className="capitalize">{feature}</Badge>)}
                    </div>
                  </div>
                )}
                <Button asChild className="w-full"><Link to="/billing/plans"><CreditCard className="mr-2 h-4 w-4" />Changer de package</Link></Button>
                <Button asChild variant="outline" className="w-full"><Link to="/billing/invoices">Factures et paiements</Link></Button>
                {generatedInvoiceHref && (
                  <Button asChild variant="outline" className="w-full">
                    <a
                      href={generatedInvoiceHref}
                      download={latestInvoice?.generated_invoice_pdf_filename ?? undefined}
                      target={latestInvoice?.generated_invoice_pdf_base64 ? undefined : "_blank"}
                      rel={latestInvoice?.generated_invoice_pdf_base64 ? undefined : "noreferrer"}
                    >
                      <FileDown className="mr-2 h-4 w-4" />Telecharger la derniere facture
                    </a>
                  </Button>
                )}
              </CardContent>
            </Card>
            <Card className="lg:col-span-2">
              <CardHeader><CardTitle>Details d'abonnement</CardTitle></CardHeader>
              <CardContent className="grid gap-3 md:grid-cols-2">
                {subscriptionDetails.map(([label, value]) => (
                  <div key={label} className="rounded-md border border-border p-3">
                    <p className="text-xs text-muted-foreground">{label}</p>
                    <p className="mt-1 font-medium">{value}</p>
                  </div>
                ))}
              </CardContent>
            </Card>
          </TabsContent>

          <TabsContent value="security" className="grid gap-6 lg:grid-cols-2">
            <Card>
              <CardHeader>
                <CardTitle>Mot de passe</CardTitle>
                <CardDescription>Le mot de passe actuel est requis avant toute modification.</CardDescription>
              </CardHeader>
              <CardContent className="space-y-4">
                <Field label="Mot de passe actuel"><Input type="password" value={passwordForm.current} onChange={(e) => setPasswordForm({ ...passwordForm, current: e.target.value })} /></Field>
                <Field label="Nouveau mot de passe"><Input type="password" value={passwordForm.next} onChange={(e) => setPasswordForm({ ...passwordForm, next: e.target.value })} /></Field>
                <Field label="Confirmer"><Input type="password" value={passwordForm.confirm} onChange={(e) => setPasswordForm({ ...passwordForm, confirm: e.target.value })} /></Field>
                <Button onClick={() => changePassword.mutate()} disabled={changePassword.isPending}>
                  {changePassword.isPending ? <Loader2 className="mr-2 h-4 w-4 animate-spin" /> : <KeyRound className="mr-2 h-4 w-4" />}
                  Changer le mot de passe
                </Button>
              </CardContent>
            </Card>
            <Card>
              <CardHeader>
                <CardTitle>Sessions et protection</CardTitle>
                <CardDescription>Derniere connexion et options de securite disponibles.</CardDescription>
              </CardHeader>
              <CardContent className="space-y-4">
                <div className="rounded-md border border-border p-3">
                  <p className="text-xs text-muted-foreground">Derniere connexion</p>
                  <p className="mt-1 font-medium">{formatDate(profile?.last_login_at)}</p>
                </div>
                <Alert>
                  <ShieldCheck className="h-4 w-4" />
                  <AlertTitle>Authentification a deux facteurs</AlertTitle>
                  <AlertDescription>{mfaSupported ? "La MFA est disponible dans la configuration d'authentification Delapresse. L'activation complete depend des parametres du projet." : "La MFA n'est pas activee dans la configuration actuelle de Delapresse."}</AlertDescription>
                </Alert>
                <Separator />
                <Button variant="outline" onClick={() => void supabase.auth.signOut({ scope: "global" })}>
                  <LogOut className="mr-2 h-4 w-4" />Deconnecter toutes les sessions
                </Button>
              </CardContent>
            </Card>
          </TabsContent>

          <TabsContent value="language">
            <Card>
              <CardHeader>
                <CardTitle>Preference linguistique</CardTitle>
                <CardDescription>Le francais est la langue officielle par defaut. L'anglais est disponible en langue secondaire.</CardDescription>
              </CardHeader>
              <CardContent className="max-w-md space-y-4">
                <Select value={form.preferred_language} onValueChange={(value) => setForm({ ...form, preferred_language: value as ProfileForm["preferred_language"] })}>
                  <SelectTrigger><SelectValue /></SelectTrigger>
                  <SelectContent>
                    {options.map((option) => <SelectItem key={option.value} value={option.value}>{option.label}</SelectItem>)}
                  </SelectContent>
                </Select>
                <Button onClick={() => saveProfile.mutate()} disabled={saveProfile.isPending}>Enregistrer la langue</Button>
              </CardContent>
            </Card>
          </TabsContent>

          <TabsContent value="notifications">
            <Card>
              <CardHeader>
                <CardTitle>Preferences de notification</CardTitle>
                <CardDescription>Les e-mails critiques de securite et d'echec de paiement restent obligatoires.</CardDescription>
              </CardHeader>
              <CardContent className="space-y-3">
                <PreferenceRow label="Factures" description="Recevoir les factures et recus apres paiement." checked={notificationPreferences?.invoice_emails ?? true} onChange={(checked) => saveNotificationPreference.mutate({ invoice_emails: checked })} />
                <PreferenceRow label="Alertes de paiement" description="Recevoir les alertes de paiement importantes." checked={notificationPreferences?.payment_alerts ?? true} onChange={(checked) => saveNotificationPreference.mutate({ payment_alerts: checked })} disabled />
                <PreferenceRow label="Rappels de renouvellement" description="Recevoir les rappels avant renouvellement." checked={notificationPreferences?.renewal_reminders ?? true} onChange={(checked) => saveNotificationPreference.mutate({ renewal_reminders: checked })} />
                <PreferenceRow label="Expiration de package" description="Recevoir les avertissements avant expiration." checked={notificationPreferences?.expiration_warnings ?? true} onChange={(checked) => saveNotificationPreference.mutate({ expiration_warnings: checked })} />
                <PreferenceRow label="Marketing" description="Recevoir les annonces produit et offres." checked={notificationPreferences?.marketing_emails ?? false} onChange={(checked) => saveNotificationPreference.mutate({ marketing_emails: checked })} />
                <PreferenceRow label="Alertes techniques" description="Recevoir les incidents et informations service." checked={notificationPreferences?.service_alerts ?? true} onChange={(checked) => saveNotificationPreference.mutate({ service_alerts: checked })} disabled />
              </CardContent>
            </Card>
          </TabsContent>

          <TabsContent value="privacy">
            <PrivacySection userEmail={user?.email ?? null} />
          </TabsContent>
        </Tabs>
      </main>
    </div>
  );
}

function PreferenceRow({ label, description, checked, disabled, onChange }: { label: string; description: string; checked: boolean; disabled?: boolean; onChange: (checked: boolean) => void }) {
  return (
    <div className="flex items-center justify-between gap-4 rounded-md border border-border p-3">
      <div>
        <p className="font-medium">{label}</p>
        <p className="text-xs text-muted-foreground">{description}</p>
      </div>
      <Switch checked={checked} disabled={disabled} onCheckedChange={onChange} />
    </div>
  );
}

function Field({ label, error, children }: { label: string; error?: string; children: React.ReactNode }) {
  return (
    <div className="space-y-2">
      <Label>{label}</Label>
      {children}
      {error && <p className="text-xs text-destructive">{error}</p>}
    </div>
  );
}

/**
 * Privacy & data-subject rights surface. RGPD Arts. 15 (access), 17
 * (erasure), 18 (restriction), 20 (portability), 21 (objection).
 *
 * Export and delete are wired to the matching edge functions; restrict
 * and object are mailto links to the DPO until we ship a structured
 * intake form.
 */
function PrivacySection({ userEmail }: { userEmail: string | null }) {
  const navigate = useNavigate();
  const [confirmOpen, setConfirmOpen] = useState(false);
  const [phrase, setPhrase] = useState("");

  type ConsentRow = { id: string; consent_type: string; version: string; accepted_at: string };
  const consentsQuery = useQuery<ConsentRow[]>({
    queryKey: ["user-consents"],
    queryFn: async () => {
      const { data, error } = await supabase
        .from("user_consents")
        .select("id, consent_type, version, accepted_at")
        .order("accepted_at", { ascending: false });
      if (error) throw error;
      return (data ?? []) as ConsentRow[];
    },
  });

  const exportMutation = useMutation({
    mutationFn: async () => {
      const { data, error } = await supabase.functions.invoke<{ url: string }>(
        "rgpd-export-account",
        { body: {} },
      );
      if (error) throw error;
      if (!data?.url) throw new Error("No download URL returned");
      return data.url;
    },
    onSuccess: (url) => {
      // Open in a new tab so the user doesn't lose context.
      window.open(url, "_blank", "noopener,noreferrer");
      toast.success("Archive prête. Téléchargement en cours.");
    },
    onError: (err) => {
      toast.error(err instanceof Error ? err.message : "Échec de l'export");
    },
  });

  const deleteMutation = useMutation({
    mutationFn: async () => {
      const { error } = await supabase.functions.invoke("rgpd-delete-account", {
        body: { confirmation_phrase: phrase },
      });
      if (error) throw error;
    },
    onSuccess: async () => {
      toast.success("Votre compte a été supprimé.");
      await supabase.auth.signOut();
      navigate("/", { replace: true });
    },
    onError: (err) => {
      toast.error(err instanceof Error ? err.message : "Échec de la suppression");
    },
  });

  const restrictionMailto = `mailto:dpo@delapresse.fr?subject=${encodeURIComponent(
    "Demande de limitation du traitement (RGPD Art. 18)",
  )}&body=${encodeURIComponent(
    "Bonjour,\n\nJe demande la limitation du traitement de mes données personnelles au titre de l'article 18 du RGPD.\n\nMotif :\n\nCordialement,",
  )}`;
  const objectionMailto = `mailto:dpo@delapresse.fr?subject=${encodeURIComponent(
    "Opposition au traitement (RGPD Art. 21)",
  )}&body=${encodeURIComponent(
    "Bonjour,\n\nJe m'oppose au traitement de mes données personnelles au titre de l'article 21 du RGPD.\n\nMotif :\n\nCordialement,",
  )}`;

  return (
    <div className="space-y-6">
      <Card>
        <CardHeader>
          <CardTitle>Confidentialité et données personnelles</CardTitle>
          <CardDescription>
            Exercez vos droits RGPD : accès, portabilité, effacement, limitation, opposition.
            Contact DPO : <a href="mailto:dpo@delapresse.fr" className="text-primary hover:underline">dpo@delapresse.fr</a>.
          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="grid gap-3 md:grid-cols-2">
            <div className="space-y-2 rounded-md border border-border p-4">
              <p className="font-medium">Exporter mes données (Art. 20)</p>
              <p className="text-xs text-muted-foreground">
                Téléchargez une archive JSON de toutes vos données personnelles (profil, articles, sites,
                factures, consentements). Lien valable 1h.
              </p>
              <Button
                onClick={() => exportMutation.mutate()}
                disabled={exportMutation.isPending}
                size="sm"
                variant="outline"
              >
                {exportMutation.isPending ? (
                  <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                ) : (
                  <Download className="mr-2 h-4 w-4" />
                )}
                Exporter
              </Button>
            </div>

            <div className="space-y-2 rounded-md border border-destructive/40 p-4">
              <p className="font-medium text-destructive">Supprimer mon compte (Art. 17)</p>
              <p className="text-xs text-muted-foreground">
                Suppression définitive. Vos factures restent conservées 10 ans (Code de commerce L123-22)
                mais sans aucune donnée nominative.
              </p>
              <Button
                onClick={() => setConfirmOpen(true)}
                size="sm"
                variant="destructive"
              >
                <Trash2 className="mr-2 h-4 w-4" />
                Supprimer mon compte
              </Button>
            </div>

            <div className="space-y-2 rounded-md border border-border p-4">
              <p className="font-medium">Limiter le traitement (Art. 18)</p>
              <p className="text-xs text-muted-foreground">
                Demandez la mise en pause d'un ou plusieurs traitements.
              </p>
              <Button asChild size="sm" variant="ghost">
                <a href={restrictionMailto}>
                  <ShieldOff className="mr-2 h-4 w-4" />
                  Écrire au DPO
                </a>
              </Button>
            </div>

            <div className="space-y-2 rounded-md border border-border p-4">
              <p className="font-medium">M'opposer au traitement (Art. 21)</p>
              <p className="text-xs text-muted-foreground">
                Opposition au traitement pour des raisons tenant à votre situation particulière.
              </p>
              <Button asChild size="sm" variant="ghost">
                <a href={objectionMailto}>
                  <Mail className="mr-2 h-4 w-4" />
                  Écrire au DPO
                </a>
              </Button>
            </div>
          </div>
        </CardContent>
      </Card>

      <Card>
        <CardHeader>
          <CardTitle>Mes consentements</CardTitle>
          <CardDescription>Historique des consentements enregistrés sur votre compte.</CardDescription>
        </CardHeader>
        <CardContent>
          {consentsQuery.isLoading ? (
            <p className="text-sm text-muted-foreground">Chargement…</p>
          ) : !consentsQuery.data?.length ? (
            <p className="text-sm text-muted-foreground">Aucun consentement enregistré pour le moment.</p>
          ) : (
            <div className="overflow-x-auto rounded-md border border-border">
              <table className="w-full text-sm">
                <thead className="bg-secondary/40 text-left text-xs uppercase tracking-wider text-muted-foreground">
                  <tr>
                    <th className="px-3 py-2">Type</th>
                    <th className="px-3 py-2">Version</th>
                    <th className="px-3 py-2">Accepté le</th>
                  </tr>
                </thead>
                <tbody>
                  {consentsQuery.data.map((row) => (
                    <tr key={row.id} className="border-t border-border">
                      <td className="px-3 py-2 font-mono text-xs">{row.consent_type}</td>
                      <td className="px-3 py-2">{row.version}</td>
                      <td className="px-3 py-2 text-muted-foreground">{new Date(row.accepted_at).toLocaleString("fr-FR")}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </CardContent>
      </Card>

      <AlertDialog open={confirmOpen} onOpenChange={(open) => { setConfirmOpen(open); if (!open) setPhrase(""); }}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Supprimer définitivement votre compte ?</AlertDialogTitle>
            <AlertDialogDescription className="space-y-2">
              <span className="block">
                Cette action est irréversible. Vous perdrez l'accès à :
              </span>
              <ul className="ml-4 list-disc text-sm">
                <li>Vos articles, sites gérés et médias.</li>
                <li>Vos préférences et consentements (les rangées seront effacées).</li>
                <li>Votre profil ({userEmail ?? "votre email"}).</li>
              </ul>
              <span className="block">
                Vos factures sont conservées 10 ans, sans nom ni email.
              </span>
              <span className="block pt-2 text-sm">
                Tapez <code className="rounded bg-secondary px-1 py-0.5 font-mono">SUPPRIMER</code> pour confirmer :
              </span>
            </AlertDialogDescription>
          </AlertDialogHeader>
          <Input
            value={phrase}
            onChange={(e) => setPhrase(e.target.value)}
            placeholder="SUPPRIMER"
            autoCapitalize="characters"
            autoComplete="off"
          />
          <AlertDialogFooter>
            <AlertDialogCancel disabled={deleteMutation.isPending}>Annuler</AlertDialogCancel>
            <AlertDialogAction
              onClick={(e) => {
                e.preventDefault();
                deleteMutation.mutate();
              }}
              disabled={phrase.trim().toUpperCase() !== "SUPPRIMER" || deleteMutation.isPending}
              className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
            >
              {deleteMutation.isPending ? <Loader2 className="mr-2 h-4 w-4 animate-spin" /> : <Trash2 className="mr-2 h-4 w-4" />}
              Supprimer mon compte
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}
